/**
 * features/course 데이터 모양 — FS-04 장애물 코스 · 커널 호스트(CSG 4.1 types.ts).
 * 기능 안(api · hooks · components)은 이 파일에서 가져오고, 바깥에 필요한 것만 index.ts 가 다시 내보낸다.
 */
import type {
  FailPattern,
  RunBundle,
  FailAnalysis,
  KernelEvent,
  ModeDefinition,
  GhostReplay,
  GhostTrack,
  LocalHost,
  RemoteInput,
} from "@picnic-field/local-host/protocol";
import type { CourseFrame, CourseViewOptions, DecorPreviewItem } from "@picnic-field/renderer";
import type { PrincipleScreenLabel } from "@picnic-field/renderer/principle";
import type { SocialApi } from "../../lib/api/socialApi";
import type { AttemptPosted, MissionGoal, VerificationStatus } from "../../lib/api/missionApi";
import type { PicoShown } from "../pico";

/** 커널 호스트(C-05) 수명 상태 — useHost · useCourseRun 공통 */
export type HostStatus = "creating" | "idle" | "loading" | "ready" | "error";

/** FS-04 한 판 HUD — 표시 값이 바뀔 때만 React 상태를 갱신한다 */
export interface Hud {
  /** 남은 시간(초) — 타이머 없으면 경과 시간 */
  clockS: number;
  countdown: boolean;
  progress: number;
  remainingM: number | null;
}

export interface PrincipleState {
  on: boolean;
  /** SU 수치 라벨 */
  numbers: boolean;
}

/** 코스 뷰 상태(data-view) — E2E 가 첫 프레임을 기다리는 데 쓴다 */
export type ViewState = "loading" | "3d" | "schematic";

/** 고스트(반투명) · 친구 기계(불투명 — FS-03-03) — 이름표는 별명만(시간 없음) */
export interface SceneMachine {
  readonly id: string;
  readonly label: string;
  readonly bundle: RunBundle;
}

export interface ResultView {
  result: RunResult;
  /** SU '멈춘 곳' — 표면 문구 · 진행 % */
  stoppedAt: { surface: string | null; pct: number } | null;
  /** 규칙 힌트 1개(미완주 · 직전과 같으면 없음) */
  hint: HintRule | null;
  /** 커널 실패 패턴(도전 기록 failPattern) */
  failPattern: FailPattern | null;
  /** 피코 반응(course-finish · course-fail — 카탈로그 규칙 표) */
  pico: PicoShown;
  /** 리플레이 저장됨 피코 반응(replay-saved) — 저장 배너의 표정 */
  savedPico?: PicoShown;
  /** 리플레이 저장 상태(S4 — 올리기 · 도전 기록) */
  save: { state: SaveState; replayId: SaveResult["replayId"]; pendingId: SaveResult["pendingId"] };
  /** 4.6 §4.3 FS-04-03 미션 띠(이 프리셋에 미션이 없으면 null) */
  mission?: MissionRunView | null;
}

/**
 * FS-04-03 미션 띠 상태(4.6 §4.3 · SCR v0.7 정정):
 *  saving(저장 중) → pending('미션 달성 · 확인 중' — 서버 재생 검증) → verified('확인됨 ✓') | failed('확인하지 못했어요 — 선생님께')
 *  unsaved(기록을 못 올림 — 저장해야 확인) · over(완주했지만 시간 목표 밖 — 규칙 힌트) · already(이미 확인됨) · none(띠 없음)
 */
export type MissionBandState =
  "saving" | "pending" | "verified" | "failed" | "unsaved" | "over" | "already" | "none";

export interface MissionRunView {
  state: MissionBandState;
  /** 이 판이 노린 목표(프리셋 같은 것) */
  goals: MissionGoal[];
  /** 확인이 오래 걸림(폴링 상한 — 띠는 '확인 중' 그대로 + 안내) */
  slow?: boolean;
  /** over — 가장 가까운 시간 목표(ms)와 이번 기록(ms) */
  goalMs?: number | null;
  timeMs?: number;
  /** 서버 검증 상태(원문 — 시험 · 계측) */
  status?: VerificationStatus;
  /** 확인 조회에 쓸 도전 기록 id(저장 뒤) */
  attemptId?: string | null;
}

/** 커널 실패 패턴(protocol FailPattern 그대로) */
export type { FailPattern };

// ── 한 판 상태(lib/flow) ──

export type RunPhase = "ready" | "driving" | "paused" | "over";

export interface RunResult {
  completed: boolean;
  /** 완주: 커널 finish.timeMs · 미완주: 마지막 프레임 시각 */
  timeMs: number;
  attempts: number;
  reason: "finish" | "timeout" | "giveup" | "rule";
}

export interface RunState {
  phase: RunPhase;
  /** 지금 막힌 포트(빨간 포트) — 표시 순서 = 막힌 순서 */
  stalls: string[];
  attempts: number;
  /** 마지막 리스폰(HUD 잠깐 표시용) */
  respawn: { reason: "fall" | "reset"; attempt: number; seq: number } | null;
  /** 이번 판에 본 FE 코드(학생에게는 토스트 한 번 — FS-C1-07) */
  fe: string[];
  /** 힌트 재료: 막힘이 난 표면 · 떨어진 표면 */
  stallSurfaces: string[];
  fallSurfaces: string[];
  /**
   * S5 실패 분석(커널 — 추락 respawn · fail 이벤트의 analysis). 판 끝 fail 의 것이 우선이고, 없으면 마지막 추락의 것.
   * 힌트(SR-ACH-04)·도전 기록 failPattern·SU '멈춘 곳'에 쓴다(수치는 학생에게 보이지 않는다).
   */
  analysis: FailAnalysis | null;
  /** 가장 멀리 간 진행률과 그때 발밑 표면(SU 결과 '멈춘 곳') */
  best: { progress: number; surface: string | null };
  /** 최근 진행 표본 */
  now: { progress: number; surface: string | null; timeMs: number };
  result: RunResult | null;
}

export type RunAction =
  | { type: "reset" }
  | { type: "start" }
  | { type: "pause" }
  | { type: "resume" }
  | { type: "sample"; progress: number; surface: string | null; timeMs: number }
  | { type: "kernel"; event: KernelEvent };

// ── 코스 프리셋(lib/presets) ──

export type Preset = ModeDefinition["presets"][number];

// ── 코스 진행 축(lib/progress) ──

export interface CourseAxis {
  start: [number, number, number];
  finish: [number, number, number];
  length: number;
}

// ── 규칙 힌트(lib/hints) ──

export interface HintRule {
  key: string;
  pattern: FailPattern;
  /** 연결된 원리(friction · inertia · center-of-mass …) — SU 문구 안 원리 용어 */
  principle?: string;
  /** 카탈로그 문구(로캘별) */
  text?: { ko?: string; en?: string } | null;
  /** 로컬 대체 규칙(카탈로그 없음) */
  local?: boolean;
}

// ── 고스트(lib/ghosts) ──

export type GhostLoad =
  | { readonly kind: "replay"; readonly replay: GhostReplay }
  | { readonly kind: "track"; readonly track: GhostTrack };

export interface GhostCandidate {
  ghostId: string;
  /** 표시 이름(별명) — 시간 없음 */
  label: string;
  completed: boolean;
  source: "friend" | "mine" | "local";
  load(): Promise<GhostLoad>;
}

export interface GhostSource {
  list(presetKey: string): Promise<GhostCandidate[]>;
}

export interface GhostContext {
  social: Pick<SocialApi, "replays" | "replay" | "replayFile" | "replayTrack">;
  runBundle: (snapshotId: string) => Promise<RunBundle>;
  modeId: string;
  /** '나의 지난 주행 n'(카탈로그 문구) */
  mineLabel: (n: number) => string;
}

// ── 리플레이 저장(lib/replaySave) ──

export type SaveState = "saving" | "saved" | "offline" | "failed" | "tooBig" | "none";

export interface SaveResult {
  state: Exclude<SaveState, "saving">;
  replayId: string | null;
  /** offline · failed 일 때 보관함 항목(다시 저장) */
  pendingId: string | null;
  /** 도전 기록을 올렸으면 그 id(4.6 미션 확인 조회) — 아직이면 없음 */
  attemptId?: string | null;
  /** 4.6 §4.3 — 미션 판이면 서버 재생 검증 대기(없으면 미션과 무관하거나 아직 못 올림) */
  verification?: AttemptPosted["verification"];
}

// ── 가짜 커널 호스트(lib/fakeHost) ──

export type SegKind = "ground" | "step" | "ramp" | "bridge" | "plateau";

export interface Segment {
  kind: SegKind;
  z0: number;
  z1: number;
  /** 시작·끝 높이(m) */
  y0: number;
  y1: number;
  /** 반폭(m) */
  halfWidth: number;
}

export interface CourseLayout {
  segments: Segment[];
  startZ: [number, number];
  finishZ: [number, number];
  length: number;
}

export interface FakeHostOptions {
  clock?: "realtime" | "manual";
  /** 속력 배수(E2E 'fast' — 기본 1) */
  speedScale?: number;
  /** 모든 프리셋 타이머를 이 초로(E2E 'short') */
  timerOverrideS?: number | null;
  /** 이 초에 FE-202(명령 예산 초과) 이벤트를 한 번 낸다 */
  fe202AtS?: number | null;
  /** 틱마다 일부러 쓰는 CPU(ms) — PT-02 부하 흉내(기본 0) */
  busyMsPerTick?: number;
  /** S4: 이 초에 입력 재생 해시가 어긋난다(FE-501 → 위치 트랙) — 리플레이 뷰어·리플레이 고스트 */
  fe501AtS?: number | null;
  /** S4: 작동한 전시 작품 중 막히는 것(작동 2초 뒤 포트 B). 기본: 별명 '쿵쿵'(와이어 예시) */
  stallExhibit?: (owner: string, label: string) => boolean;
  /** S4: 동시 작동 한도(기본 6) */
  activeLimit?: number;
  /**
   * S5: 전시관 받침대를 모드 정의(gallery.pedestals — 실제 커널과 같은 자리 · 2m 간격, 앞줄 왼쪽부터 채움)에 둔다.
   * 기본(false)은 가짜 격자(가운데 정렬 0.9m). 작품이 적을 때 첫 카메라 확인용(?fs03=slots).
   */
  gallerySlots?: boolean;
}

export interface FakeLocalHost extends LocalHost {
  /** manual 시계: ms 만큼 틱을 돌린다 */
  advance(ms: number): void;
  readonly layout: CourseLayout | null;
  readonly lastInput: RemoteInput;
  /** 지금 무엇을 돌리는가(시험용) */
  readonly kind: "none" | "course" | "gallery" | "play" | "replay";
  readonly speed: number;
}

// ── 원리 보기 뷰 조각(components/Principle) ──

/** 원리 보기 뷰 조각(코스 뷰 · 전시관 뷰 공통 — 가짜 뷰는 없을 수 있다) */
export interface PrincipleViewLike {
  setPrinciple?(p: { on: boolean; numbers?: boolean; owners?: readonly string[] | null }): void;
  principleLabels?(out: PrincipleScreenLabel[]): number;
}

// ── 커널 호스트 · 코스 뷰 의존성(lib/deps) ──

export interface CourseViewLike extends PrincipleViewLike {
  readonly ready: Promise<{ meModel: "glb" | "placeholder" }>;
  update(frame: CourseFrame): void;
  setQuality(q: "high" | "low"): void;
  /** 확대 · 축소 — 카메라 거리 배율(가짜 뷰는 없을 수 있다) */
  setZoom?(factor: number): void;
  /** 둘러보기 — 기계 둘레로 도는 각(도) · 높이 배율 − 1(기구 무대) */
  setOrbit?(yawDeg: number, tilt: number): void;
  onPaint(cb: (t: number, tick: number) => void): () => void;
  /** 몸체 위 이름표 자리(캔버스 CSS px) — 화면 밖이면 null */
  project?(bodyIndex: number, lift?: number): { x: number; y: number } | null;
  stats(): { painted: number; drawCalls: number; pixelRatio: number };
  /** 장면 밝기 · 빛 색(micro:bit 센서 월드 — 렌더러 setAmbience) */
  setAmbience?(a: { readonly exposure?: number; readonly tint?: string | null }): void;
  /** 기구 무대 꾸밈 데이터 바꾸기(운영 콘솔 미리보기 — 장면을 줬을 때만) · 그리지 않은 항목을 돌려준다 */
  setStageDecor?(data: unknown): readonly { path: string; message: string }[];
  /** 기구 무대 꾸밈(장면을 줬을 때만) — 손님 반응 · 신남 */
  readonly stage?: {
    react(kind: "star" | "cheer" | "bonus" | "oops"): void;
    setExcite(v: number): void;
  } | null;
  dispose(): void;
}

/** 전시관 뷰(C-02 createGalleryView) — 받침대 ≤30 · 작동 중인 작품만 관절 모형, 나머지는 인스턴스 */
export interface GalleryViewLike extends PrincipleViewLike {
  readonly ready: Promise<unknown>;
  update(frame: CourseFrame): void;
  setActive(owners: readonly string[]): void;
  setViewpoint(
    p: { x: number; z: number },
    opts?: { back?: number; heading?: number; lookUp?: number },
  ): void;
  /** P3-S2 릴레이 구간 상태 색(렌더러 — 다시 준비는 중립색) · 없으면 색 표시 없음 */
  setSectionStatus?(statuses: readonly (string | null | undefined)[]): void;
  setQuality(q: "high" | "low"): void;
  /** 확대 · 축소 — 카메라 거리 배율(가짜 뷰는 없을 수 있다) */
  setZoom?(factor: number): void;
  onPaint(cb: (t: number, tick: number) => void): () => void;
  project?(bodyIndex: number, lift?: number): { x: number; y: number } | null;
  stats(): { painted: number; drawCalls: number; pixelRatio: number };
  dispose(): void;
}

export interface GalleryViewRequest {
  bodies: CourseViewOptions["bodies"];
  exhibits: readonly {
    owner: string;
    glbUrl: string | null;
    /** S5 LOD(선택 — 정지 L2 · 작동 L1 · 없으면 L0) */
    lod?: CourseViewOptions["me"]["lod"];
    appearance?: CourseViewOptions["me"]["appearance"];
    palette?: CourseViewOptions["me"]["palette"];
  }[];
  quality?: "high" | "low";
  background?: string | null;
  /**
   * SR-PHY-02 렌더 보간(기본 false) — 틱마다 커널 상태를 주는 화면(전시관 로컬 호스트 · 룸 리플레이 재시뮬레이션)만 켠다.
   * 룸 · 칠판은 RoomConnection.frame() 이 이미 스냅샷을 보간한다.
   */
  interpolate?: boolean;
  /** 우리 반 룸 장면(2026-10-07 — 렌더러 courseScenes "room-schoolyard") · 룸 · 룸 리플레이 · 칠판 */
  roomScene?: string;
  /** 자유 놀이터 교사 꾸밈(2026-10-11 · meta.mode.decor — 보기 전용) · 올린 모델 GLB 주소 */
  roomDecor?: unknown;
  stageModelUrl?: (model: string) => Promise<string | null>;
}

/** 우리 반 룸 장면 키(렌더러는 늦게 불러오므로 문자열로 둔다) */
export const ROOM_SCENE = "room-schoolyard";

export type GalleryViewFactory = (
  canvas: HTMLCanvasElement,
  opts: GalleryViewRequest,
) => Promise<GalleryViewLike>;

export type CourseViewFactory = (
  canvas: HTMLCanvasElement,
  opts: Omit<CourseViewOptions, "dracoPath">,
) => Promise<CourseViewLike>;

/** 꾸밈 한 개 미리보기(렌더러 createDecorPreview · 운영 콘솔 무대 꾸밈 인벤토리 — 2026-10-08) */
export interface DecorPreviewLike {
  show(item: DecorPreviewItem | null): Promise<boolean>;
  size(): { x: number; y: number; z: number } | null;
  dispose(): void;
}

export interface CourseDeps {
  createHost: () => Promise<LocalHost>;
  createView: CourseViewFactory;
  createGalleryView: GalleryViewFactory;
  createDecorPreview: (canvas: HTMLCanvasElement) => Promise<DecorPreviewLike>;
  /** 고스트 후보 — 기본: 제어 평면 리플레이 + 아직 못 올린 내 주행(오프라인 보관함) */
  ghostSource: (ctx: GhostContext) => Promise<GhostSource>;
  /** 가짜 모드인가(지연·통계 노출용) */
  fake: boolean;
}
